/**
 * Native HYPE Asset Utilities
 *
 * Provides utilities for handling native HYPE token in the EVC/Euler ecosystem.
 * When interacting with Euler vaults that use WHYPE as underlying, users can
 * deposit native HYPE which gets wrapped via EVC batch calls.
 */

import { Address } from 'viem'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { NATIVE_ASSET_MOCK_ADDRESS } from '@/config/consts'
import { whypeConfig } from '@/config/contracts-generated'
import { getContractAddress } from '@/domain/hooks/useContractAddress'
import { type Asset, type NativeAsset, NATIVE_ADDRESS } from '@eulerxyz/wagmi-evc'

// =============================================================================
// Constants
// =============================================================================

/**
 * Native HYPE placeholder address used in the app for native asset tracking.
 * This is the same as NATIVE_ADDRESS from wagmi-evc but with proper casing.
 */
export const NATIVE_HYPE_ADDRESS = NATIVE_ASSET_MOCK_ADDRESS

/**
 * Common WHYPE contract address on HyperEVM (chain 999)
 */
export const WHYPE_ADDRESS_HYPEREVM = CheckedAddress('0x5555555555555555555555555555555555555555')

// =============================================================================
// Type Guards
// =============================================================================

/**
 * Check if an address is the native HYPE placeholder
 */
export function isNativeHypeAddress(address: Address): boolean {
  return address.toLowerCase() === NATIVE_ASSET_MOCK_ADDRESS.toLowerCase()
}

/**
 * Check if an address is WHYPE (wrapped HYPE)
 */
export function isWhypeAddress(address: Address, chainId: number): boolean {
  try {
    const whypeAddress = getContractAddress(whypeConfig.address, chainId)
    return address.toLowerCase() === whypeAddress.toLowerCase()
  } catch {
    return false
  }
}

/**
 * Check if a vault's underlying asset is WHYPE
 * This determines if native HYPE deposits are supported
 */
export function isWhypeVault(underlyingAsset: Address, chainId: number): boolean {
  return isWhypeAddress(underlyingAsset, chainId)
}

// =============================================================================
// Token Creation
// =============================================================================

/**
 * Create a native HYPE Token object for use in the app
 */
export function createNativeHypeToken(priceUsd = '0'): Token {
  return new Token({
    symbol: TokenSymbol('HYPE'),
    name: 'Hype',
    decimals: 18,
    address: NATIVE_HYPE_ADDRESS,
    unitPriceUsd: priceUsd,
  })
}

/**
 * Create a native HYPE Asset object for wagmi-evc
 */
export function createNativeHypeAsset(): NativeAsset {
  return {
    address: NATIVE_ADDRESS,
    name: 'Hype',
    symbol: 'HYPE',
    decimals: 18,
  }
}

/**
 * Create a WHYPE Asset object for wagmi-evc
 */
export function createWhypeAsset(chainId: number): Asset {
  const whypeAddress = getContractAddress(whypeConfig.address, chainId)
  return {
    address: whypeAddress,
    name: 'Wrapped Hype',
    symbol: 'WHYPE',
    decimals: 18,
  }
}

// =============================================================================
// Address Conversion
// =============================================================================

/**
 * Get the WHYPE address for a chain
 */
export function getWhypeAddress(chainId: number): CheckedAddress {
  return getContractAddress(whypeConfig.address, chainId)
}

/**
 * Convert native HYPE address to WHYPE address for contract calls
 * Used when the underlying contract expects WHYPE but UI shows native HYPE
 */
export function nativeToWhype(address: Address, chainId: number): Address {
  if (isNativeHypeAddress(address)) {
    return getWhypeAddress(chainId)
  }
  return address
}

/**
 * Get deposit options for a vault
 * Returns both native HYPE and WHYPE if the vault underlying is WHYPE
 */
export function getVaultDepositOptions(
  underlyingAsset: Address,
  underlyingToken: Token,
  chainId: number,
  nativeHypePrice = '0',
): Token[] {
  if (isWhypeAddress(underlyingAsset, chainId)) {
    // Vault accepts WHYPE - offer both native HYPE and WHYPE
    const nativeHypeToken = createNativeHypeToken(nativeHypePrice)
    return [nativeHypeToken, underlyingToken]
  }
  // Standard vault - only underlying token
  return [underlyingToken]
}
